import { useEffect, useMemo, forwardRef, useState, ReactNode } from 'react';
import { ContextMenu } from 'app/components/ChakraTableList';
import { Box, BoxProps } from '@chakra-ui/react';
import { Props as InfiniteScrollProps } from 'react-infinite-scroll-component';
import {
  LoadingBody,
  LoadingRow,
  TBody,
  Table,
  Th,
  Thead,
} from 'app/components/Table';
import {
  ExpandedState,
  Row,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ContextMenuProps } from 'app/components/ChakraTableList/ContextMenu/types';
import {
  getColumnSizing,
  saveColumnSizing,
  getTableWidth,
  getHeader,
  getColumnWidth,
} from './utils';
import { BaseTableType } from './types';
import { STICKY_TABLE_CLASS, STICKY_THEAD_CLASS } from './constants';
import { StickyTableThead } from './StickyTableThead';
import { StickyTableBodyWrapper } from './StickyTableBodyWrapper';

interface StickyTableProps<T> extends BoxProps {
  tableKey: string;
  data: T[];
  columns: T[];
  infiniteScrollProps: InfiniteScrollProps;
  loadingMore: boolean;
  contextMenuProps?: ContextMenuProps;
  loading?: boolean;
  rowRenderer: (row: Row<T>, index: number) => React.ReactNode;
  getSubRows?: (row: T) => T[];
  footer?: ReactNode;
  showHeader?: boolean;
}

export const StickyTable = forwardRef(
  <T extends BaseTableType>(
    {
      tableKey,
      data,
      columns,
      infiniteScrollProps,
      loadingMore,
      contextMenuProps,
      rowRenderer,
      loading,
      getSubRows,
      footer,
      showHeader = true,
      ...boxProps
    }: StickyTableProps<T>,
    ref
  ) => {
    const sizing = useMemo(() => getColumnSizing()?.[tableKey], [tableKey]);

    const [expanded, setExpanded] = useState<ExpandedState>(true);

    const table = useReactTable({
      data,
      columns,
      columnResizeMode: 'onChange',
      initialState: {
        columnSizing: sizing?.columnSizing || {},
      },
      columnResizeDirection: 'ltr',
      getCoreRowModel: getCoreRowModel(),
      onStateChange: () => {
        saveColumnSizing(table, tableKey);
      },

      state: {
        expanded,
      },
      getExpandedRowModel: getExpandedRowModel(),
      onExpandedChange: setExpanded,
      getSubRows,
    });

    const theadEl = document.querySelector(`.${STICKY_THEAD_CLASS}`);
    const tableEl = document.querySelector(`.${STICKY_TABLE_CLASS}`);

    useEffect(() => {
      if (tableEl && theadEl) {
        const scrollHead = () => {
          theadEl.scrollLeft = tableEl.scrollLeft;
        };
        const scrollBody = () => {
          tableEl.scrollLeft = theadEl.scrollLeft;
        };
        tableEl.addEventListener('scroll', scrollHead);
        theadEl.addEventListener('scroll', scrollBody);
        return () => {
          tableEl.removeEventListener('scroll', scrollHead);
          theadEl.removeEventListener('scroll', scrollBody);
        };
      }
      return () => {};
    }, [tableEl, theadEl]);

    const gapWidth = (ref.current?.clientWidth || 0) - table.getTotalSize();

    return (
      <Box ref={ref} flex={1} overflow="clip" {...boxProps}>
        <Box display={ref.current ? 'block' : 'none'}>
          {showHeader && (
            <StickyTableThead
              tableKey={tableKey}
              columns={columns}
              table={table}
              gapWidth={gapWidth}
            />
          )}
          <Box overflowX="auto">
            <StickyTableBodyWrapper infiniteScrollProps={infiniteScrollProps}>
              {/* mb="1px" required for the drag row to bottom */}
              <Table width={getTableWidth(table, gapWidth)} mb="1px">
                {contextMenuProps && (
                  <ContextMenu contextMenuProps={contextMenuProps} />
                )}
                <Thead sx={{ visibility: 'collapse' }}>
                  {columns.map((c, i) => {
                    const header = getHeader(table, i);
                    return (
                      <Th
                        key={c.accessorKey}
                        tableKey={tableKey}
                        isAvatar={c.isAvatar}
                        isOptions={c.isOptions}
                        sortColumnProps={
                          header.column.getIsResizing()
                            ? undefined
                            : c.sortColumnProps
                        }
                        position="relative"
                        width={`${getColumnWidth(header, gapWidth)}px`}
                        header={c.enableResizing ? header : undefined}
                        className={c.accessorKey === 'gap' ? 'gap' : ''}
                        sx={c.sx}
                      >
                        <Box>{c.header}</Box>
                      </Th>
                    );
                  })}
                </Thead>
                {loading ? (
                  <LoadingBody columns={columns} />
                ) : (
                  <TBody>
                    {table.getRowModel()?.rows?.map(rowRenderer)}
                    {footer}
                    {loadingMore && <LoadingRow columns={columns} />}
                  </TBody>
                )}
              </Table>
            </StickyTableBodyWrapper>
          </Box>
        </Box>
      </Box>
    );
  }
) as any;
